import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Alert,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { useStore } from '../context/StoreContext';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

// 公司資訊常數（可從 tenant settings 取得）
const DEFAULT_COMPANY_INFO = {
  name: '昀美國際貿易有限公司',
  taxId: '42762120',
  phone: '04-7380668',
  address: '彰化市彰南路三段481-1號',
  email: 'service@urstore.tw',
  lineId: '@454ygmfg',
  serviceHours: '週一至週五 9:00-18:00'
};

function Contact() {
  const { t } = useTranslation('common');
  const { storeSettings } = useStore();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  // 使用 tenant settings 或預設值
  const companyInfo = {
    name: storeSettings?.company_name || DEFAULT_COMPANY_INFO.name,
    taxId: storeSettings?.tax_id || DEFAULT_COMPANY_INFO.taxId,
    phone: storeSettings?.contact_phone || DEFAULT_COMPANY_INFO.phone,
    address: storeSettings?.contact_address || DEFAULT_COMPANY_INFO.address,
    email: storeSettings?.contact_email || DEFAULT_COMPANY_INFO.email,
    lineId: storeSettings?.line_id || DEFAULT_COMPANY_INFO.lineId,
    serviceHours: storeSettings?.service_hours || DEFAULT_COMPANY_INFO.serviceHours
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      // 發送聯絡表單到後端
      await api.post('/email/contact', formData);
      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (err) {
      setError(err.response?.data?.error || t('contact.send_failed'));
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    return formData.name.trim() !== '' &&
           formData.email.trim() !== '' &&
           formData.message.trim() !== '';
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* 頁面標題 */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          {t('contact.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('contact.subtitle')}
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* 左側：聯絡資訊 */}
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon color="primary" />
                {t('contact.info_title')}
              </Typography>

              <List>
                <ListItem>
                  <ListItemIcon>
                    <PhoneIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('contact.phone')}
                    secondary={companyInfo.phone}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <EmailIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('contact.email')}
                    secondary={companyInfo.email}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <LocationIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('contact.address')}
                    secondary={companyInfo.address}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <TimeIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('contact.service_hours')}
                    secondary={companyInfo.serviceHours}
                  />
                </ListItem>
              </List>

              <Divider sx={{ my: 2 }} />

              {/* LINE 客服 */}
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {t('contact.line_support')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('contact.line_add_friend')}
                </Typography>
                <Button
                  variant="contained"
                  color="success"
                  href={`https://line.me/R/ti/p/${companyInfo.lineId}`}
                  target="_blank"
                  sx={{
                    bgcolor: '#06C755',
                    '&:hover': { bgcolor: '#05b04d' }
                  }}
                >
                  LINE ID: {companyInfo.lineId}
                </Button>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* 公司資訊 */}
              <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('contact.company_name')}: {companyInfo.name}<br />
                  {t('contact.tax_id')}: {companyInfo.taxId}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 右側：聯絡表單 */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              {t('contact.form_title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('contact.form_subtitle')}
            </Typography>

            {success && (
              <Alert
                severity="success"
                icon={<CheckCircleIcon />}
                sx={{ mb: 3 }}
              >
                {t('contact.send_success')}
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label={t('contact.form_name')}
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('contact.form_phone')}
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('contact.form_subject')}
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    multiline
                    rows={5}
                    label={t('contact.form_message')}
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder={t('contact.form_message_placeholder')}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={loading || !isFormValid()}
                    startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                  >
                    {loading ? t('contact.sending') : t('contact.send')}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Grid>
      </Grid>

      {/* Google 地圖區域（選用） */}
      <Paper sx={{ mt: 4, p: 0, overflow: 'hidden' }}>
        <Box sx={{ p: 2, bgcolor: '#f5f5f5' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {t('contact.how_to_reach')}
          </Typography>
        </Box>
        <Box
          sx={{
            width: '100%',
            height: 300,
            bgcolor: '#e0e0e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <iframe
            title={t('contact.map_title')}
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3636.5!2d120.5!3d24.05!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjTCsDAzJzAwLjAiTiAxMjDCsDMwJzAwLjAiRQ!5e0!3m2!1szh-TW!2stw!4v1"
            width="100%"
            height="300"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </Box>
      </Paper>
    </Container>
  );
}

export default Contact;
